import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { User } from './user';
export { User };
import { userSchema } from '@schemas/business-objects/user';
import { CurriculumFormArray } from './curriculum-form-group';
import { ResourceFormArray } from './resource-form-group';
import { ResourceSetFormArray } from './resource-set-form-group';
import { RoleAssignmentFormArray } from './role-assignment-form-group';
import { StudentFormGroup } from './student-form-group';
import { TermFormArray } from './term-form-group';
import { TestResultFormArray } from './test-result-form-group';
import { TutorFormGroup } from './tutor-form-group';
import { UserActivityPeriodFormArray } from './user-activity-period-form-group';

export class UserFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    email: SchemaFormControl;
    roleAssignments: RoleAssignmentFormArray;
    activityPeriods: UserActivityPeriodFormArray;
    tutor: TutorFormGroup;
    customerAccountManagerTerms: TermFormArray;
    literacyManagerTerms: TermFormArray;
    siteLeaderTerms: TermFormArray;
    student: StudentFormGroup;
    createdResources: ResourceFormArray;
    publishedResources: ResourceFormArray;
    archivedResources: ResourceFormArray;
    createdResourceSets: ResourceSetFormArray;
    publishedResourceSets: ResourceSetFormArray;
    archivedResourceSets: ResourceSetFormArray;
    testResults: TestResultFormArray;
    createdCurriculums: CurriculumFormArray;
    publishedCurriculums: CurriculumFormArray;
    archivedCurriculums: CurriculumFormArray;
  };

  declare value: Partial<User>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(userSchema, User, data, options);
  }

  get object(): User {
    return new User(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'roleAssignments':
        return new RoleAssignmentFormArray();
      case 'activityPeriods':
        return new UserActivityPeriodFormArray();
      case 'tutor':
        return new TutorFormGroup();
      case 'customerAccountManagerTerms':
        return new TermFormArray();
      case 'literacyManagerTerms':
        return new TermFormArray();
      case 'siteLeaderTerms':
        return new TermFormArray();
      case 'student':
        return new StudentFormGroup();
      case 'createdResources':
        return new ResourceFormArray();
      case 'publishedResources':
        return new ResourceFormArray();
      case 'archivedResources':
        return new ResourceFormArray();
      case 'createdResourceSets':
        return new ResourceSetFormArray();
      case 'publishedResourceSets':
        return new ResourceSetFormArray();
      case 'archivedResourceSets':
        return new ResourceSetFormArray();
      case 'testResults':
        return new TestResultFormArray();
      case 'createdCurriculums':
        return new CurriculumFormArray();
      case 'publishedCurriculums':
        return new CurriculumFormArray();
      case 'archivedCurriculums':
        return new CurriculumFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<User[]> {
    return User.find(filter);
  }

  static async findOne(filter?: any): Promise<User | null> {
    return User.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<User> {
    return User.findById(id, filter);
  }

  static async create(data: Partial<User>): Promise<User> {
    return User.create(data as any);
  }

  static async updateById(id: number, updates: Partial<User>): Promise<User> {
    return User.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return User.deleteById(id);
  }

  static async currentUser(filter?: any): Promise<UserFormGroup | null> {
    const result = await User.currentUser(filter);
    if (!result) return null;
    const fg = new UserFormGroup();
    fg._populate(result);
    return fg;
  }
}

export class UserFormArray extends PersistedFormArray {
  readonly entityName = 'User' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(userSchema, User, options);
  }

  protected override createItemGroup() {
    return new UserFormGroup();
  }

  get array(): User[] {
    return this.controls.map((group: UserFormGroup) => group.object);
  }
}

export class UserPersistedArray extends PersistedArray<User> {
  readonly entityName = 'User' as const;

  constructor(options?: PersistedArrayOptions) {
    super(User, options);
  }
}
